import classNames from 'classnames';
import { memo, useCallback } from 'react';
import { Button } from '../../components/Button';
import { LedgerRow } from '../../components/CreditLedger/LedgerRow';
import { Modal } from '../../components/Modal';
import { useFormatDate } from '../../hooks/useFormatDate';
import { useDownloadInvoice } from '../../hooks/useDownloadInvoice';
import { T, useT } from '../../translation/src';
import { RefreshInvoicesButton } from './RefreshInvoicesButton';
import { CreditLedgerEntrySource, CreditLedgerEntryType, SuperadminFullCreditLedgerQuery, useSuperadminFullCreditLedgerQuery } from '../../types/graphqlTypes';

const PAGE_SIZE = 50;

type FullLedgerEntry = SuperadminFullCreditLedgerQuery['superadminCreditLedger']['data'][number];

// A charge for a processed project: the credits it took, the project (by name while it still exists), and
// the points counted.
const ChargeRow_: React.FC<{ entry: FullLedgerEntry }> = ({ entry }) => {
  const { formatDate } = useFormatDate();
  const deletedProject = useT('deleted project', { swc: true });
  const charge = useT('charge', { swc: true });
  const title = entry.projectName ?? (entry.projectId ? deletedProject : entry.note || charge);

  return (
    <div className="flex items-start gap-3 py-2 text-sm border-b border-gray-200 dark:border-gray-700 last:border-b-0">
      <span className="px-2 py-0.5 rounded-full text-xs font-medium font-mono w-12 text-center shrink-0 bg-error-red-200 text-error-red-700 dark:bg-error-red-700 dark:text-error-red-200">-{entry.amount}</span>
      <div className="flex flex-col grow min-w-0">
        <span className={classNames('truncate text-gray-700 dark:text-gray-300', !entry.projectName && entry.projectId && 'italic')}>{title}</span>
        <span className="text-xs text-gray-500">
          {formatDate(entry.createdAt)}
          {entry.pointCountTotal != null && (
            <span className="ml-1">
              · <T _str="{count} points" args={{ count: Math.round(entry.pointCountTotal).toLocaleString() }} />
            </span>
          )}
          {!!entry.shortfall && (
            <span className="ml-1 text-error-red-500">
              · <T _str="shortfall" />: {entry.shortfall}
            </span>
          )}
          {entry.projectId && <span className="ml-1 font-mono">· {entry.projectId}</span>}
        </span>
      </div>
    </div>
  );
};
const ChargeRow = memo(ChargeRow_);

export interface LedgerModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
  organisationName: string;
}
// The superadmin's view of an organisation's whole credit ledger, newest first: the grants and fees of the
// purchase history, and every charge for a processed project in between.
const LedgerModal_: React.FC2<LedgerModalProps> = ({ onClose, open, organisationId, organisationName }) => {
  const { data, fetchMore, loading, refetch } = useSuperadminFullCreditLedgerQuery({
    variables: { organisationId, pagination: { limit: PAGE_SIZE } },
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });
  const { downloadInvoice, downloadingEntryId } = useDownloadInvoice({ organisationId });

  const page = data?.superadminCreditLedger;
  const entries = page?.data || [];
  const nextCursor = page?.nextCursor;
  const onLoadMore = useCallback(() => {
    if (!nextCursor) return;
    void fetchMore({
      variables: { organisationId, pagination: { limit: PAGE_SIZE, cursor: nextCursor } },
      updateQuery: (previous, { fetchMoreResult }) => ({
        superadminCreditLedger: { ...fetchMoreResult.superadminCreditLedger, data: [...previous.superadminCreditLedger.data, ...fetchMoreResult.superadminCreditLedger.data] },
      }),
    });
  }, [fetchMore, nextCursor, organisationId]);

  return (
    <Modal.Container open={open} onClose={onClose} panelClassName="w-full max-w-lg">
      <Modal.Header onClose={onClose}>
        <span className="flex items-center gap-2 min-w-0">
          <span className="truncate">
            <T _str="ledger" swc /> - {organisationName}
          </span>
          <RefreshInvoicesButton organisationId={organisationId} onRefreshed={refetch} />
        </span>
      </Modal.Header>
      <Modal.Body className="px-1">
        {page && (
          <div className="px-1 pb-2 text-xs text-gray-500">
            <T _str="{count} entries" args={{ count: page.total }} />
          </div>
        )}
        {/* Room for the scrollbar, so prices are not under it. */}
        <div className="flex flex-col min-h-40 max-h-[32rem] overflow-y-auto py-1 pr-3">
          {entries.length === 0 && !loading && (
            <div className="flex items-center justify-center flex-grow text-sm text-gray-500">
              <T _str="the ledger is empty" swc />
            </div>
          )}
          {entries.map((entry) =>
            entry.type === CreditLedgerEntryType.Charge && entry.source !== CreditLedgerEntrySource.Removed ? (
              <ChargeRow key={entry.id} entry={entry} />
            ) : (
              <LedgerRow key={entry.id} entry={entry} showExpiry showRemaining onDownloadInvoice={downloadInvoice} downloading={downloadingEntryId === entry.id} />
            ),
          )}
          {page?.hasMore && (
            <div className="flex justify-center py-2">
              <Button variant="tertiary" size="xs" onClick={onLoadMore} loading={loading}>
                <T _str="load more" swc />
              </Button>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal.Container>
  );
};

export const LedgerModal = memo(LedgerModal_);
